import type { Trigger } from '@libs/enums';
import { Characters, type ItemAttributes, ItemName, Locale, SingleValue } from '@ps-smart-cp/ppfw-client';

const RULE_NAME_MAX_LEN = 64;

interface Schema {
  ruleName: string;
  trigger: Trigger;
}

interface Record {
  ruleName: string;
  trigger: string;
}

const attributes = {
  ruleName: Characters.preset.freeFormat({ maxLen: RULE_NAME_MAX_LEN, required: true }),
  trigger: SingleValue.shortValue(true),
} satisfies ItemAttributes<Schema>;

const names = {
  maintenance: ItemName.of([Locale.ja, 'ルールマスタメンテナンス'], [Locale.en, 'Rule Master Maintenance']),
  register: ItemName.of([Locale.ja, 'ルールマスタ登録'], [Locale.en, 'Rule Master Register']),
  change: ItemName.of([Locale.ja, 'ルールマスタ変更'], [Locale.en, 'Rule Master Change']),
  detail: ItemName.of([Locale.ja, 'ルール詳細'], [Locale.en, 'Rule Master Detail']),
  execution: ItemName.of([Locale.ja, 'ルール詳細'], [Locale.en, 'Rule Master Detail']),
  executionConfirmation: ItemName.of([Locale.ja, '実行確認'], [Locale.en, 'Confirm Execution']),

  rule: ItemName.of([Locale.ja, 'ルール'], [Locale.en, 'Rule']),
  ruleName: ItemName.of([Locale.ja, 'ルール名称'], [Locale.en, 'Rule Name']),
  trigger: ItemName.of([Locale.ja, 'トリガー'], [Locale.en, 'Trigger']),

  triggerConfig: ItemName.of([Locale.ja, 'トリガー設定'], [Locale.en, 'Trigger Settings']),
  targetInput: ItemName.of([Locale.ja, 'ターゲット入力'], [Locale.en, 'Target Input']),
  /** Target 入力の値をどう決めるか。実際の参照先は別の項目で選ぶ。 */
  bindingMode: ItemName.of([Locale.ja, '入力方法'], [Locale.en, 'Input Method']),
  literalSource: ItemName.of([Locale.ja, '固定値'], [Locale.en, 'Fixed Value']),
  referenceSource: ItemName.of([Locale.ja, 'アクションから取得'], [Locale.en, 'From Action']),
  /** 参照する Trigger Output。 */
  referencePath: ItemName.of([Locale.ja, '参照項目'], [Locale.en, 'Referenced Item']),
  /** 固定値の入力欄。項目名は binding row 側が表示する。 */
  bindingValue: ItemName.of([Locale.ja, '値'], [Locale.en, 'Value']),
  /** 固定値の editor を持たない型の項目。画面では編集させない。 */
  unsupportedValue: ItemName.of(
    [Locale.ja, 'この項目はこの画面では設定できません'],
    [Locale.en, 'This item cannot be set on this screen'],
  ),

  execute: ItemName.of([Locale.ja, '実行'], [Locale.en, 'Execute']),

  description: ItemName.of([Locale.ja, '説明'], [Locale.en, 'Description']),

  executionConfirmationMsg: ItemName.of(
    [Locale.ja, 'ターゲット： {0} を実行します。よろしいですか？'],
    [Locale.en, 'Executing target: {0}. Are you sure?'],
  ),

  stopConfirmation: ItemName.of([Locale.ja, '中止確認'], [Locale.en, 'Confirm Stop']),
  stopConfirmationMsg: ItemName.of(
    [Locale.ja, 'ターゲット： {0} の実行を中止します。よろしいですか？'],
    [Locale.en, 'Stopping execution of target: {0}. Are you sure?'],
  ),
};

const ruleMst = {
  attributes,
  names,
};

interface RuleMst {
  schema: Schema;
  record: Record;
}

export type { RuleMst };
export { ruleMst };
